import { DefaultUISettings, TCFUISettings, UI_VARIANT } from '@usercentrics/cmp-browser-sdk';
import { FunctionalComponent, h } from 'preact';
import { RefContainer, RowDescription, ToggleRow } from './VirtualList.style';
import CategoryCardContent from '../CategoryCardContent';
import { CategoryProps } from '../CategoryHandler/MapCategories';
import ExpandableCard from '../ExpandableCard/ExpandableCard';
import { InlineToggle } from '../Toggles';
import { ScrollToId } from '../../App.helpers';
import { TabPanelTitle } from '../Tabs';
import { VerticalSpacer } from '../Spacers/VerticalSpacer';
import { memo } from 'preact/compat';
import { useRef } from 'preact/hooks';
import { useSettingsContext } from '../../contexts/SettingsContext';
import { useUiContext } from '../../contexts/UiContext';
import { useVirtualListItem } from '../../hooks';

type ItemProps = {
  categories: CategoryProps[];
  getItemSize: (index: number) => number;
  hasTopSpace?: boolean;
  index: number;
  id: number;
  setItemSize: (index: number, height: number) => void;
  showServicesToggles: boolean;
  showToggles: boolean;
  title?: string;
  showOnlyConsentToggle?: boolean;
  scrollToId?: ScrollToId;
};

const VirtualCategoryItem: FunctionalComponent<ItemProps> = memo(
  ({
    categories,
    hasTopSpace = false,
    id,
    index,
    scrollToId,
    getItemSize,
    setItemSize,
    showToggles,
    title,
    showOnlyConsentToggle = false,
    showServicesToggles,
  }) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const { updateComponentSize } = useVirtualListItem({ getItemSize, index, itemRef, setItemSize });
    const { uiVariant } = useUiContext();
    const { settings } = useSettingsContext();
    const ui = settings.ui as TCFUISettings;
    const type =
      uiVariant === UI_VARIANT.TCF
        ? (settings.ui as TCFUISettings).secondLayer.tabs.purposes.label
        : (settings.ui as DefaultUISettings).secondLayer.tabs.categories.label;

    // Optional: render of a space as first item on the list
    if (hasTopSpace && index === 0) {
      return (
        <RefContainer ref={itemRef}>
          <VerticalSpacer custom={16} />
        </RefContainer>
      );
    }

    // Optional: render a title as a list item
    if (title != null && ((!hasTopSpace && id === 0) || (hasTopSpace && id === 1))) {
      return (
        <RefContainer ref={itemRef}>
          <TabPanelTitle>{title}</TabPanelTitle>
        </RefContainer>
      );
    }

    let categoryIndex = id;

    if (hasTopSpace) {
      categoryIndex -= 1;
    }

    if (title != null) {
      categoryIndex -= 1;
    }

    const { category, checked, onCategoryToggle, onServiceToggle } = categories[categoryIndex];
    const toggles = [];

    if (showToggles) {
      toggles.push({
        checked,
        id: category.slug,
        isDisabled: category.isEssential,
        onToggle: onCategoryToggle,
      });
    }

    if (showOnlyConsentToggle) {
      return (
        <RefContainer ref={itemRef}>
          <ToggleRow data-testid="uc-tcf-categories">
            <RowDescription>{category.label}</RowDescription>
            {!category.isEssential && !ui.firstLayer.hideToggles && (
              <InlineToggle checked={checked} id={category.slug} onToggle={onCategoryToggle} />
            )}
          </ToggleRow>
        </RefContainer>
      );
    }

    return (
      <RefContainer ref={itemRef}>
        <ExpandableCard
          description={category.description}
          onToggleExpandable={updateComponentSize}
          isExpandedOnInit={scrollToId === category.slug}
          title={category.label}
          hideToggle={false}
          toggles={toggles}
          type={type}
        >
          {category.services
            .sort((a, b) => {
              return a.name > b.name ? 1 : -1;
            })
            .map((service) => {
              return (
                <CategoryCardContent
                  category={category}
                  detailedCategoryToggleAriaLabel={ui.ariaLabels.serviceInCategoryDetails}
                  onToggle={onServiceToggle}
                  service={service}
                  showServicesToggles={showServicesToggles}
                />
              );
            })}
        </ExpandableCard>
      </RefContainer>
    );
  },
);

export default VirtualCategoryItem;
